import type { Arena } from '@/types/arena.type'

import { useMemo } from 'react'
import { ImageOff } from 'lucide-react'
import { fDate } from '@/utils/format-date'
import { Separator } from '@/components/ui/separator'
import ExpandableDescription from '@/components/expandable-description'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ArenaDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  arena: Arena | null
}

export default function ArenaDetailDialog({
  open,
  onOpenChange,
  arena,
}: ArenaDetailDialogProps) {
  const basicInfo = useMemo(
    () =>
      arena
        ? [
            { label: 'ชื่อสนาม', value: arena.name },
            { label: 'เบอร์ติดต่อ', value: arena.contactNumber || '-' },
          ]
        : [],
    [arena],
  )

  const systemInfo = useMemo(
    () =>
      arena
        ? [
            { label: 'วันที่สร้าง', value: fDate(arena.createdAt) },
            { label: 'อัปเดตล่าสุด', value: fDate(arena.updatedAt) },
          ]
        : [],
    [arena],
  )

  const renderInfoSection = (
    title: string,
    items: Array<{ label: string; value: string }>,
  ) => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        {items.map((item, index) => (
          <div key={item.label || index}>
            <div className="text-muted-foreground mb-1">{item.label}</div>
            <div className="font-medium truncate max-w-[300px]">
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderMediaSection = () => {
    if (!arena?.mediaUrls || arena.mediaUrls.length === 0) {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">รูปภาพสนาม</h3>
          <div className="flex items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <ImageOff className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-muted-foreground">ไม่มีรูปภาพ</p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">รูปภาพสนาม</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {arena.mediaUrls.map((media, index) => (
            <div key={index} className="space-y-2">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={media.url}
                  alt={media.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-xs text-muted-foreground">
                <p className="font-medium truncate">{media.name}</p>
                <p>
                  {media.size ? `${(media.size / 1024).toFixed(1)} KB` : '-'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>รายละเอียดสนามแข่งขัน</DialogTitle>
          <DialogDescription>
            แสดงรายละเอียดทั้งหมดของสนามแข่งขัน
          </DialogDescription>
        </DialogHeader>

        {arena && (
          <div className="space-y-6">
            {renderInfoSection('ข้อมูลพื้นฐาน', basicInfo)}
            <Separator />
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">คำอธิบาย</h3>
              <ExpandableDescription
                description={arena.description || '-'}
                maxLength={200}
                className="text-black"
              />
            </div>
            <Separator />
            {renderMediaSection()}
            <Separator />
            {renderInfoSection('ข้อมูลระบบ', systemInfo)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
